import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Flex,
  Text,
  Button,
  Input,
  Select,
  Textarea,
  FormControl,
  FormLabel,
  FormErrorMessage,
  VStack,
  HStack,
  SimpleGrid,
  Divider,
  useToast,
  useColorModeValue,
  Spinner,
  Badge,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import {
  projectService,
  portfolioService,
  programService,
} from "../services/edosService";

const PROJECT_TYPES = [
  "Implementation",
  "Support",
  "Internal",
  "Migration",
  "Enhancement",
];
const BILLING_TYPES = ["Fixed", "T&M", "Retainer"];
const PRIORITIES = ["Low", "Medium", "High", "Critical"];

const PRIORITY_COLOR = {
  Low: "blue",
  Medium: "yellow",
  High: "orange",
  Critical: "red",
};

export default function ProjectCreatePage() {
  const navigate = useNavigate();
  const toast = useToast();

  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const sectionBg = useColorModeValue("gray.50", "gray.700");

  const [form, setForm] = useState({
    projectName: "",
    projectType: "Implementation",
    billingType: "Fixed",
    priority: "Medium",
    portfolioId: "",
    programId: "",
    startDate: "",
    targetEndDate: "",
    description: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [portfolios, setPortfolios] = useState([]);
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    portfolioService
      .list()
      .then((r) => setPortfolios(r.data.data || r.data.portfolios || []))
      .catch(() => {});
    programService
      .list()
      .then((r) => setPrograms(r.data.data || r.data.programs || []))
      .catch(() => {});
  }, []);

  const set = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    if (errors[field]) setErrors((err) => ({ ...err, [field]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.projectName.trim()) e.projectName = "Project name is required";
    if (
      form.startDate &&
      form.targetEndDate &&
      form.startDate > form.targetEndDate
    ) {
      e.targetEndDate = "End date must be after start date";
    }
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.portfolioId) delete payload.portfolioId;
      if (!payload.programId) delete payload.programId;
      if (!payload.startDate) delete payload.startDate;
      if (!payload.targetEndDate) delete payload.targetEndDate;
      if (!payload.description) delete payload.description;

      const res = await projectService.create(payload);
      const created = res.data.data || res.data.project || res.data;
      toast({ title: "Project created successfully", status: "success" });
      navigate(`/edos/projects/${created._id}`);
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Failed to create project",
        status: "error",
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box p={6} maxW="100%" mx="auto">
      {/* Page Header */}
      <Flex align="center" mb={6} gap={3}>
        <Button
          leftIcon={<ArrowBackIcon />}
          variant="ghost"
          onClick={() => navigate("/edos/projects")}
        >
          Back to Projects
        </Button>
      </Flex>

      <Box
        bg={bg}
        border="1px"
        borderColor={borderColor}
        borderRadius="lg"
        overflow="hidden"
      >
        {/* Title Bar */}
        <Box
          px={6}
          py={4}
          bg={sectionBg}
          borderBottom="1px"
          borderColor={borderColor}
        >
          <Text fontSize="xl" fontWeight="bold">
            Create New Project
          </Text>
          <Text fontSize="sm" color="gray.500" mt={1}>
            Fill in the details below to create a new project
          </Text>
        </Box>

        <Box p={6}>
          <VStack spacing={8} align="stretch">
            {/* Basic Info */}
            <Box>
              <Text
                fontWeight="semibold"
                mb={4}
                color="blue.600"
                fontSize="sm"
                textTransform="uppercase"
                letterSpacing="wide"
              >
                Basic Information
              </Text>
              <VStack spacing={4} align="stretch">
                <FormControl isRequired isInvalid={!!errors.projectName}>
                  <FormLabel>Project Name</FormLabel>
                  <Input
                    value={form.projectName}
                    onChange={set("projectName")}
                    placeholder="e.g. Customer Portal v2"
                    size="md"
                  />
                  <FormErrorMessage>{errors.projectName}</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={form.description}
                    onChange={set("description")}
                    placeholder="Brief description of the project scope, goals, and deliverables..."
                    rows={3}
                    resize="vertical"
                  />
                </FormControl>
              </VStack>
            </Box>

            <Divider />

            {/* Classification */}
            <Box>
              <Text
                fontWeight="semibold"
                mb={4}
                color="blue.600"
                fontSize="sm"
                textTransform="uppercase"
                letterSpacing="wide"
              >
                Classification
              </Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Project Type</FormLabel>
                  <Select
                    value={form.projectType}
                    onChange={set("projectType")}
                  >
                    {PROJECT_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Billing Type</FormLabel>
                  <Select
                    value={form.billingType}
                    onChange={set("billingType")}
                  >
                    {BILLING_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>
                    Priority{" "}
                    <Badge colorScheme={PRIORITY_COLOR[form.priority]} ml={1}>
                      {form.priority}
                    </Badge>
                  </FormLabel>
                  <Select value={form.priority} onChange={set("priority")}>
                    {PRIORITIES.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </SimpleGrid>
            </Box>

            <Divider />

            {/* Timeline */}
            <Box>
              <Text
                fontWeight="semibold"
                mb={4}
                color="blue.600"
                fontSize="sm"
                textTransform="uppercase"
                letterSpacing="wide"
              >
                Timeline
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel>Start Date</FormLabel>
                  <Input
                    type="date"
                    value={form.startDate}
                    onChange={set("startDate")}
                  />
                </FormControl>

                <FormControl isInvalid={!!errors.targetEndDate}>
                  <FormLabel>Target End Date</FormLabel>
                  <Input
                    type="date"
                    value={form.targetEndDate}
                    onChange={set("targetEndDate")}
                  />
                  <FormErrorMessage>{errors.targetEndDate}</FormErrorMessage>
                </FormControl>
              </SimpleGrid>
            </Box>

            <Divider />

            {/* Hierarchy (optional) */}
            <Box>
              <Text
                fontWeight="semibold"
                mb={4}
                color="blue.600"
                fontSize="sm"
                textTransform="uppercase"
                letterSpacing="wide"
              >
                Hierarchy (Optional)
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel>
                    Portfolio{" "}
                    <Text as="span" color="gray.400" fontSize="xs">
                      (optional)
                    </Text>
                  </FormLabel>
                  <Select
                    value={form.portfolioId}
                    onChange={set("portfolioId")}
                    placeholder="— None —"
                  >
                    {portfolios.map((p) => (
                      <option key={p._id} value={p._id}>
                        {p.name || p.portfolioName}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>
                    Program{" "}
                    <Text as="span" color="gray.400" fontSize="xs">
                      (optional)
                    </Text>
                  </FormLabel>
                  <Select
                    value={form.programId}
                    onChange={set("programId")}
                    placeholder="— None —"
                  >
                    {programs
                      .filter(
                        (pg) =>
                          !form.portfolioId ||
                          pg.portfolioId === form.portfolioId,
                      )
                      .map((pg) => (
                        <option key={pg._id} value={pg._id}>
                          {pg.name || pg.programName}
                        </option>
                      ))}
                  </Select>
                </FormControl>
              </SimpleGrid>
            </Box>
          </VStack>
        </Box>

        {/* Footer Actions */}
        <Box
          px={6}
          py={4}
          bg={sectionBg}
          borderTop="1px"
          borderColor={borderColor}
        >
          <HStack justify="flex-end" spacing={3}>
            <Button
              variant="ghost"
              onClick={() => navigate("/edos/projects")}
              isDisabled={submitting}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              isLoading={submitting}
              loadingText="Creating..."
              px={8}
            >
              Create Project
            </Button>
          </HStack>
        </Box>
      </Box>
    </Box>
  );
}
